import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { useState, useEffect, useCallback, useMemo, useRef, type DragEvent } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import DocumentViewer from '@/components/DocumentViewer';
import RecognitionScheduleModal from '@/components/RecognitionScheduleModal';
import EventHistory from '@/components/EventHistory';
import CategoryDropdown from '@/components/CategoryDropdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Trash2, ArrowLeft, CheckCircle, AlertCircle, BadgeCheckIcon, Loader2, Calendar, Upload, RefreshCw } from 'lucide-react';

import { formatAmountDetailed, formatDate, formatQuantity, formatVatRate } from '@/utils/formatters';
import { apiFetch, getFileUrl } from '@/utils/api';

import { useI18n } from '@/hooks/useI18n';
import { TagSelector } from '@/components/TagSelector';
import { Tag } from '@/components/TagBadge';
import { VendorCombobox } from '@/components/VendorCombobox';

// Search params for invoice detail (e.g., returnTo for navigation)
const invoiceDetailSearchSchema = z.object({
    returnTo: z.string().optional(),
    nav_direction: z.string().optional(),
    edit: z.string().optional(), // '1' or 'true' opens edit mode initially
})

interface Vendor {
    id: string;
    name: string;
}

interface Category {
    id: string;
    name: string;
}

interface CostCenter {
    id: string;
    name: string;
    is_active: boolean;
}

interface Product {
    id: string;
    name: string;
    status?: 'draft' | 'active' | 'inactive';
    cost_category?: Category;
    revenue_category?: Category;
    base_unit?: {
        id: string;
        name: string;
        abbreviation: string;
    };
}

interface LineItem {
    id?: string;
    description: string;
    quantity: string | number;
    unit_price: string | number;                 // HUF unit price (calculated)
    unit_price_original_currency?: string | number; // Original currency unit price (source)
    line_total: string | number;
    net_amount: string | number;
    vat_rate: string | number;
    vat_amount: string | number;
    unit_of_measure: string;
    unit_of_measure_id?: string;
    unit_of_measure_ref?: any;
    product_code: string;
    category_id: string;
    category?: Category;
    product?: Product;
    product_match_status?: string;
    is_new?: boolean;
}

interface Invoice {
    id: string;
    invoice_number?: string;
    vendor_id?: string;
    vendor?: Vendor;
    customer_id?: string;
    customer?: Vendor;
    cost_center_id?: string;
    cost_center?: CostCenter;
    nav_credentials_id?: string | null;  // NAV account ID, null means manually uploaded invoice
    nav_direction?: string;
    total_amount?: number;
    net_amount?: number;
    tax_amount?: number;
    currency: string;  // Required for RecognitionScheduleModal
    exchange_rate?: number;
    invoice_date?: string;
    service_date?: string;
    due_date?: string;
    payment_method?: string;
    payment_method_id?: string;
    payment_status?: string;
    status?: string;
    file_path?: string;
    mime_type?: string;
    original_filename?: string;
    file_size?: string;
    created_at: string;
    line_items?: LineItem[];
    validation_errors?: any[];
    original_xml?: string;  // Raw XML received from NAV
    has_invoice_photo?: boolean;  // Whether this invoice has an associated photo
}

interface FormData {
    invoice_number: string;
    vendor_id: string;
    cost_center_id: string;
    customer_id?: string;  // Receiving company for inbound invoices
    total_amount: string;
    net_amount: string;
    tax_amount: string;
    currency: string;
    exchange_rate?: string; // HUF per 1 unit of currency
    invoice_date: string;
    service_date: string;
    due_date: string;
    payment_method: string;
}

interface Message {
    type: 'success' | 'error';
    text: string;
}

const toDateInputValue = (value?: string | null): string => {
    if (!value) return '';
    const trimmed = value.trim();
    if (!trimmed) return '';

    // HTML date inputs require strict YYYY-MM-DD formatting.
    const exactDateMatch = trimmed.match(/^\d{4}-\d{2}-\d{2}$/);
    if (exactDateMatch) return exactDateMatch[0];

    const embeddedDateMatch = trimmed.match(/\d{4}-\d{2}-\d{2}/);
    if (embeddedDateMatch) return embeddedDateMatch[0];

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString().split('T')[0];
};

export default function InvoiceDetailPage() {
    const navigate = useNavigate();
    const params = useParams({ from: '/invoices/$id' });
    const routerState = useRouterState();
    const searchParams = routerState.location.search as any;
    const { t } = useI18n();

    const { id } = params;
    const isNew = id === 'new';

    const [invoice, setInvoice] = useState<Invoice | null>(null);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [companies, setCompanies] = useState<Vendor[]>([]);  // Companies for customer selector
    const [availableCurrencies, setAvailableCurrencies] = useState<string[]>(['HUF']);

    const [units, setUnits] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [reprocessing, setReprocessing] = useState<boolean>(false);
    const [rerunningCategorization, setRerunningCategorization] = useState<boolean>(false);
    const [unmatchingPhoto, setUnmatchingPhoto] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);
    const [uploadingDocument, setUploadingDocument] = useState<boolean>(false);
    const [isDocumentDragActive, setIsDocumentDragActive] = useState<boolean>(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
    const [message, setMessage] = useState<Message | null>(null);
    const documentFileInputRef = useRef<HTMLInputElement | null>(null);
    // Initialize edit mode from search param (edit=1 or edit=true)
    const initialEditMode = (() => {
        try {
            const sp = new URLSearchParams(searchParams);
            const v = (sp.get('edit') || '').toLowerCase();
            return v === '1' || v === 'true';
        } catch {
            return false;
        }
    })();
    const [editMode, setEditMode] = useState<boolean>(isNew ? true : initialEditMode);
    const [recognitionScheduleModalOpen, setRecognitionScheduleModalOpen] = useState<boolean>(false);
    const [hasRecognitionSchedule, setHasRecognitionSchedule] = useState<boolean>(false);
    const [formData, setFormData] = useState<FormData>({
        invoice_number: '',
        vendor_id: '',
        cost_center_id: '',
        total_amount: '',
        net_amount: '',
        tax_amount: '',
        currency: 'HUF',
        invoice_date: '',
        service_date: '',
        due_date: '',
        payment_method: '',
    });
    const [vatRates, setVatRates] = useState<number[]>([]);
    const [lineItems, setLineItems] = useState<LineItem[]>([]);
    const [defaultUnitId, setDefaultUnitId] = useState<string | null>(null);
    const [invoiceTags, setInvoiceTags] = useState<Tag[]>([]);

    // Check nav_direction from both URL search params and invoice data
    // URL search params take ABSOLUTE precedence (for when navigating from sales invoices)
    const isOutbound = useMemo(() => {
        // If nav_direction is in URL, use it (takes precedence)
        if (searchParams?.nav_direction) {
            return searchParams.nav_direction.toUpperCase() === 'OUTBOUND';
        }
        // Otherwise fall back to invoice data
        const navDirection = invoice?.nav_direction || '';
        return navDirection.toUpperCase() === 'OUTBOUND';
    }, [searchParams?.nav_direction, invoice?.nav_direction]);


    useEffect(() => {
        if (id) {
            // Fetch settings first; only fetch invoice if not creating a new one
            fetchSettings().then(() => {
                if (!isNew) fetchInvoice();
                else {
                    const stub: Invoice = {
                        id: 'new',
                        status: 'new',
                        currency: 'HUF',
                        created_at: new Date().toISOString(),
                        nav_direction: isOutbound ? 'OUTBOUND' : 'INBOUND',
                        has_invoice_photo: false,
                        line_items: []
                    };
                    setInvoice(stub);
                    setLoading(false);
                }
            });
            fetchCategories();
            fetchCostCenters();
            fetchCompanies();
            fetchUnits();
            // Fallback vendor fetch in case invoice doesn't load properly
            fetchVendors();
        }
    }, [id, isNew, isOutbound]);

    // Handle ESC key to go back to invoices list (only when not in edit mode)
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !editMode) {
                handleGoBack();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [editMode]);

    // Handle navigation back to list with preserved state
    const handleGoBack = () => {
        const returnTo = searchParams?.returnTo as string;
        if (returnTo) {
            // Navigate to the full URL with search params preserved
            navigate({ to: returnTo });
        } else {
            // Default: go back to invoices list
            navigate({ to: '/invoices' });
        }
    };



    const fetchInvoice = async () => {
        setLoading(true);
        try {
            const response = await apiFetch(`/api/invoices/${id}`);
            if (response.ok) {
                const data = await response.json();
                setInvoice(data);
                setFormData({
                    invoice_number: data.invoice_number || '',
                    vendor_id: data.vendor_id || '',
                    cost_center_id: data.cost_center_id || '',
                    customer_id: data.customer_id || '',
                    total_amount: data.total_amount !== null && data.total_amount !== undefined ? String(data.total_amount) : '',
                    net_amount: data.net_amount !== null && data.net_amount !== undefined ? String(data.net_amount) : '',
                    tax_amount: data.tax_amount !== null && data.tax_amount !== undefined ? String(data.tax_amount) : '',
                    currency: data.currency || 'HUF',
                    invoice_date: toDateInputValue(data.invoice_date),
                    service_date: toDateInputValue(data.service_date),
                    due_date: toDateInputValue(data.due_date),
                    payment_method: data.payment_method || '',
                });
                // Process line items to set proper default selection
                const processedLineItems = (data.line_items || []).map((item: any) => ({
                    ...item,
                    // Priority: unit_of_measure_ref > unit_of_measure_id > default from settings > 'keep_legacy'
                    unit_of_measure_id: item.unit_of_measure_ref?.id || item.unit_of_measure_id || defaultUnitId || 'keep_legacy',
                    // Category selection source depends on direction:
                    // - OUTBOUND (sales): use product.revenue_category
                    // - INBOUND (purchases): use product.cost_category
                    category_id: isOutbound
                        ? (item.product?.revenue_category?.id || item.category_id || '')
                        : (item.product?.cost_category?.id || item.category_id || ''),
                }));
                setLineItems(processedLineItems);

                // Fetch vendors with current vendor ID to ensure it's included in dropdown
                // After loading invoice, fetch available MNB currencies for this date
                try {
                    const effDate = toDateInputValue(data.invoice_date);
                    const resp = await apiFetch(`/api/currencies/available-mnb-currencies${effDate ? `?effective_date=${encodeURIComponent(effDate)}` : ''}`);
                    if (resp.ok) {
                        const payload = await resp.json();
                        const codes: string[] = payload.codes || [];
                        // Always include HUF as a selectable currency
                        const setCodes = Array.from(new Set([...(codes || []), 'HUF'])).sort();
                        setAvailableCurrencies(setCodes);
                    }
                } catch (e) {
                    console.error('Failed to fetch available MNB currencies', e);
                }

                if (data.vendor_id) {
                    fetchVendors(data.vendor_id);
                } else {
                    fetchVendors();
                }

                // Check if invoice has a recognition schedule
                checkRecognitionSchedule();

                // Load tags for this invoice
                loadInvoiceTags();
            } else {
                setMessage({ type: 'error', text: 'Failed to load invoice' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Network error occurred' });
        } finally {
            setLoading(false);
        }
    };

    const loadInvoiceTags = async () => {
        try {
            const response = await apiFetch(`/api/tags/entities/invoices/${id}`);
            if (response.ok) {
                const assignments = await response.json();
                setInvoiceTags(assignments.map((a: any) => a.tag));
            }
        } catch (error) {
            console.error('Error loading invoice tags:', error);
        }
    };

    const checkRecognitionSchedule = async () => {
        try {
            const response = await apiFetch(`/api/recognition-schedules/invoice/${id}`);
            if (response.ok) {
                const data = await response.json();
                setHasRecognitionSchedule(!!data);
            }
        } catch (error) {
            // Silently fail - not critical
            console.error('Failed to check recognition schedule:', error);
        }
    };

    const fetchVendors = async (currentVendorId?: string) => {
        try {
            // Fetch maximum allowed vendors to increase chances of including current vendor
            // This solves the pagination issue where current vendor might not be on first page
            const response = await apiFetch('/api/vendors/?limit=1000');
            if (response.ok) {
                const data = await response.json();
                let vendorsList = data.vendors || data;

                // If we have a current vendor ID that's not in the list, fetch it specifically
                const vendorId = currentVendorId || invoice?.vendor_id;
                if (vendorId && !vendorsList.find((v: Vendor) => v.id === vendorId)) {
                    try {
                        console.log('Current vendor not found in list, fetching specifically:', vendorId);
                        const vendorResponse = await apiFetch(`/api/vendors/${vendorId}`);
                        if (vendorResponse.ok) {
                            const currentVendor = await vendorResponse.json();
                            // Add the current vendor to the beginning of the list
                            vendorsList = [currentVendor, ...vendorsList];
                            console.log('Successfully added current vendor to list:', currentVendor.name);
                        }
                    } catch (vendorError) {
                        console.error('Error fetching current vendor:', vendorError);
                    }
                }

                setVendors(vendorsList);
            }
        } catch (error) {
            console.error('Error fetching vendors:', error);
        }
    };

    const fetchCategories = async () => {
        try {
            const endpoint = isOutbound ? '/api/revenue-categories/' : '/api/categories/';
            const response = await apiFetch(endpoint);
            if (response.ok) {
                const data = await response.json();
                setCategories(data);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchCostCenters = async () => {
        try {
            const response = await apiFetch('/api/cost-centers/');
            if (response.ok) {
                const data = await response.json();
                // Only include active cost centers
                setCostCenters(data.filter((cc: CostCenter) => cc.is_active));
            }
        } catch (error) {
            console.error('Error fetching cost centers:', error);
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await apiFetch('/api/vendors/?used_as_customer_inbound=true&limit=100');
            if (response.ok) {
                const data = await response.json();
                const vendorsList = data.vendors || data;
                setCompanies(vendorsList);
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    const fetchUnits = async () => {
        try {
            const response = await apiFetch('/api/units/');
            if (response.ok) {
                const data = await response.json();
                // Only include active units
                setUnits(data.filter((unit: any) => unit.is_active));
            }
        } catch (error) {
            console.error('Error fetching units:', error);
        }
    };

    const fetchSettings = async () => {
        try {
            const response = await apiFetch('/api/settings');
            if (response.ok) {
                const data = await response.json();
                setDefaultUnitId(data.settings?.default_unit_of_measure_id || null);

                // Fetch VAT percentages from organization settings
                const vatPercentagesStr = data.settings?.vat_percentages;
                if (vatPercentagesStr) {
                    try {
                        const parsed = JSON.parse(vatPercentagesStr);
                        if (Array.isArray(parsed)) {
                            setVatRates(parsed);
                        }
                    } catch (e) {
                        console.error('Error parsing VAT percentages:', e);
                        setVatRates([0, 5, 18, 27]); // Default Hungarian VAT rates
                    }
                } else {
                    setVatRates([0, 5, 18, 27]); // Default Hungarian VAT rates
                }
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            setVatRates([0, 5, 18, 27]); // Default Hungarian VAT rates
        }
    };

    const handleInputChange = (field: keyof FormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };





    const handleLineItemChange = (index: number, field: keyof LineItem, value: string) => {
        setLineItems(prev => prev.map((item, i) => {
            if (i === index) {
                const updatedItem = { ...item, [field]: value };

                // Handle unit selection - allow keeping legacy units or selecting from taxonomy
                if (field === 'unit_of_measure_id') {
                    if (value === 'keep_legacy') {
                        // Keep the original unit_of_measure string, clear the ID
                        updatedItem.unit_of_measure_id = '';
                        // Don't change unit_of_measure - keep original value
                        updatedItem.unit_of_measure_ref = null;
                    } else if (value === 'none' || value === '') {
                        // Clear both fields
                        updatedItem.unit_of_measure_id = '';
                        updatedItem.unit_of_measure = '';
                        updatedItem.unit_of_measure_ref = null;
                    } else {
                        // Select from taxonomy
                        const selectedUnit = units.find((unit: any) => unit.id === value);
                        if (selectedUnit) {
                            updatedItem.unit_of_measure = selectedUnit.abbreviation;
                            updatedItem.unit_of_measure_ref = selectedUnit;
                        }
                    }
                }

                return updatedItem;
            }
            return item;
        }));
    };



    const addLineItem = () => {
        const newItem: LineItem = {
            id: `temp-${Date.now()}`, // Temporary ID for new items
            description: '',
            quantity: '',
            unit_price: '',
            line_total: '',
            net_amount: '',
            vat_rate: '',
            vat_amount: '',
            unit_of_measure: '',
            unit_of_measure_id: 'none',
            product_code: '',
            category_id: '',
            is_new: true // Flag to identify new items
        };
        setLineItems(prev => [...prev, newItem]);
    };

    const removeLineItem = (index: number) => {
        setLineItems(prev => prev.filter((_, i) => i !== index));
    };

    const calculateLineTotal = (quantity: string | number, unitPrice: string | number, vatRate: string | number): string => {
        if (!quantity || !unitPrice) return '';
        const netTotal = parseFloat(quantity.toString()) * parseFloat(unitPrice.toString());
        const vatAmount = netTotal * (parseFloat(vatRate.toString()) || 0) / 100;
        return (netTotal + vatAmount).toFixed(2);
    };


    const handleLineItemCalculation = (index: number, field: string, value: string) => {
        const updatedItems = [...lineItems];
        const item = { ...updatedItems[index] };

        // Update the changed field
        (item as any)[field] = value;

        // Auto-calculate related fields
        if (field === 'quantity' || field === 'unit_price' || field === 'vat_rate') {
            const quantity = parseFloat(item.quantity?.toString() || '0') || 0;
            const unitPrice = parseFloat(item.unit_price?.toString() || '0') || 0;
            const vatRate = parseFloat(item.vat_rate?.toString() || '0') || 0;

            if (quantity && unitPrice) {
                const netAmount = quantity * unitPrice;
                const vatAmount = netAmount * vatRate / 100;
                const lineTotal = netAmount + vatAmount;

                item.net_amount = netAmount.toFixed(2);
                item.vat_amount = vatAmount.toFixed(2);
                item.line_total = lineTotal.toFixed(2);
            }
        }

        updatedItems[index] = item;
        setLineItems(updatedItems);
    };

    // Validation function to check if all fields have valid values and track field errors
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: boolean }>({});

    // Separate function to get field errors without causing re-renders
    const getFieldErrors = useCallback(() => {
        const newFieldErrors: { [key: string]: boolean } = {};

        // Check main form fields for empty or default values
        if (!formData.invoice_number?.trim()) {
            newFieldErrors['invoice_number'] = true;
        }
        if (!formData.vendor_id || formData.vendor_id === '') {
            newFieldErrors['vendor_id'] = true;
        }
        if (!isOutbound && (!formData.cost_center_id || formData.cost_center_id === '')) {
            newFieldErrors['cost_center_id'] = true;
        }
        if (!formData.currency || formData.currency === '') {
            newFieldErrors['currency'] = true;
        }
        if (!formData.invoice_date) {
            newFieldErrors['invoice_date'] = true;
        }

        // Check line items for required fields and default values
        lineItems.forEach((item, index) => {
            if (!item.description?.trim()) {
                newFieldErrors[`line_item_${index}_description`] = true;
            }
            if (!item.quantity || item.quantity === '' || parseFloat(item.quantity.toString()) == 0) {
                newFieldErrors[`line_item_${index}_quantity`] = true;
            }
            if (!item.unit_price || item.unit_price === '') {
                newFieldErrors[`line_item_${index}_unit_price`] = true;
            }
            // Allow 'keep_legacy' as valid - it will be handled during save
            if (!item.unit_of_measure_id || item.unit_of_measure_id === 'none') {
                newFieldErrors[`line_item_${index}_unit_of_measure_id`] = true;
            }
            if (!item.category_id || item.category_id === '') {
                newFieldErrors[`line_item_${index}_category_id`] = true;
            }
            if (item.vat_rate === '' || item.vat_rate === null || item.vat_rate === undefined) {
                newFieldErrors[`line_item_${index}_vat_rate`] = true;
            }
        });

        return newFieldErrors;
    }, [formData, lineItems]);

    // Update field errors when form data changes
    useEffect(() => {
        const newFieldErrors = getFieldErrors();
        setFieldErrors(newFieldErrors);
    }, [getFieldErrors]);

    const validateForm = () => {
        const errors: string[] = [];

        // Check main form fields for empty or default values
        if (!formData.invoice_number?.trim()) {
            errors.push('Invoice number is required');
        }
        if (!formData.vendor_id || formData.vendor_id === '') {
            errors.push('Vendor must be selected');
        }
        if (!isOutbound && (!formData.cost_center_id || formData.cost_center_id === '')) {
            errors.push('Cost center must be selected');
        }
        if (!formData.currency || formData.currency === '') {
            errors.push('Currency must be selected');
        }
        if (!formData.invoice_date) {
            errors.push('Invoice date is required');
        }
        // Payment method is optional, so don't validate it

        // Check line items for required fields and default values
        lineItems.forEach((item, index) => {
            if (!item.description?.trim()) {
                errors.push(`Line item ${index + 1}: Description is required`);
            }
            if (!item.quantity || item.quantity === '' || parseFloat(item.quantity.toString()) == 0) {
                errors.push(`Line item ${index + 1}: Valid quantity is required`);
            }
            if (!item.unit_price || item.unit_price === '') {
                errors.push(`Line item ${index + 1}: Unit price is required`);
            }
            // Allow 'keep_legacy' as valid - it will be handled during save
            if (!item.unit_of_measure_id || item.unit_of_measure_id === 'none') {
                errors.push(`Line item ${index + 1}: Unit of measure must be selected`);
            }
            if (!item.category_id || item.category_id === '') {
                errors.push(`Line item ${index + 1}: Category must be selected`);
            }
            if (item.vat_rate === '' || item.vat_rate === null || item.vat_rate === undefined) {
                errors.push(`Line item ${index + 1}: VAT rate is required`);
            }

        });

        return errors;
    };

    // Helper function to create a new unit of measure
    const createUnitOfMeasure = async (unitName: string) => {
        try {
            // Determine unit type based on common patterns
            const unitNameLower = unitName.toLowerCase();
            let unitType = 'count'; // default

            if (unitNameLower.includes('kg') || unitNameLower.includes('gram') || unitNameLower.includes('pound') || unitNameLower.includes('oz')) {
                unitType = 'weight';
            } else if (unitNameLower.includes('liter') || unitNameLower.includes('litre') || unitNameLower.includes('ml') || unitNameLower.includes('gallon')) {
                unitType = 'volume';
            } else if (unitNameLower.includes('meter') || unitNameLower.includes('metre') || unitNameLower.includes('cm') || unitNameLower.includes('inch') || unitNameLower.includes('foot')) {
                unitType = 'length';
            }

            const response = await apiFetch('/api/units/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: unitName,
                    abbreviation: unitName.length <= 10 ? unitName : unitName.substring(0, 10),
                    unit_type: unitType,
                    is_base_unit: true, // Create as base unit for simplicity
                    is_active: true
                }),
            });

            if (response.ok) {
                const newUnit = await response.json();
                console.log('Created new unit:', newUnit);
                return newUnit;
            } else {
                console.error('Failed to create unit:', await response.text());
                return null;
            }
        } catch (error) {
            console.error('Error creating unit:', error);
            return null;
        }
    };
    const handleCurrencyChange = async (newCurrency: string) => {
        try {
            // Update currency immediately
            setFormData((prev) => ({ ...prev, currency: newCurrency }));

            // Determine effective date for rate lookup
            const effDate = formData.invoice_date || toDateInputValue(invoice?.invoice_date);
            const rateResp = await apiFetch(`/api/currencies/exchange-rate/${encodeURIComponent(newCurrency)}/HUF${effDate ? `?effective_date=${encodeURIComponent(effDate)}` : ''}`);
            if (!rateResp.ok) return; // Leave values untouched if rate not found
            const rateData = await rateResp.json();
            const rate = Number(rateData.rate || rateData?.["rate"] || 0);
            if (!rate || !isFinite(rate)) return;

            // Store exchange rate
            setFormData((prev) => ({ ...prev, exchange_rate: String(rate) }));

            // Recalculate line items and totals using new rate.
            // Treat existing invoice amounts (prefer original currency unit price) as being in the selected currency.
            const updatedItems = (lineItems || []).map((item: any) => {
                const qty = Number(item.quantity || 0);
                const srcUnit = item.unit_price_original_currency != null ? Number(item.unit_price_original_currency) : Number(item.unit_price || 0);
                const unitHuf = isFinite(srcUnit) ? srcUnit * rate : 0;
                const net = isFinite(qty) ? qty * unitHuf : Number(item.net_amount || 0);
                const vatRate = Number(item.vat_rate || 0);
                const vat = isFinite(net) ? (net * (isFinite(vatRate) ? vatRate : 0) / 100) : Number(item.vat_amount || 0);
                const total = (isFinite(net) ? net : 0) + (isFinite(vat) ? vat : 0);

                return {
                    ...item,
                    unit_price: round2(unitHuf),
                    net_amount: round2(net),
                    vat_amount: round2(vat),
                    line_total: round2(total),
                };
            });

            setLineItems(updatedItems);

            // Recompute invoice totals from line items
            const sum = (arr: any[], key: string) => arr.reduce((acc, it) => acc + Number(it[key] || 0), 0);
            const newNet = sum(updatedItems, 'net_amount');
            const newVat = sum(updatedItems, 'vat_amount');
            const newTotal = sum(updatedItems, 'line_total');

            setFormData((prev) => ({
                ...prev,
                net_amount: String(round2(newNet)),
                tax_amount: String(round2(newVat)),
                total_amount: String(round2(newTotal)),
            }));
        } catch (e) {
            console.error('Failed to change currency and recalc amounts', e);
        }
    };

    const round2 = (val: number) => {
        return Math.round((Number(val || 0) + Number.EPSILON) * 100) / 100;
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);

        // Validate form before saving
        const validationErrors = validateForm();
        if (validationErrors.length > 0) {
            setMessage({
                type: 'error',
                text: `Please fix the following issues:\n${validationErrors.join('\n')}`
            });
            setSaving(false);
            return;
        }

        try {
            // Collect draft products that have categories selected for activation
            const draftProductsToActivate: Array<{ productId: string, categoryId: string }> = [];

            // Process line items and handle "keep_legacy" units
            const processedLineItems: any[] = [];
            for (const item of lineItems) {
                const processedItem = {
                    ...item,
                    // Remove temporary fields for new items
                    id: item.is_new ? undefined : item.id,
                    is_new: undefined
                };

                // Handle unit_of_measure_id properly for API validation
                if (item.unit_of_measure_id === 'keep_legacy') {
                    if (item.unit_of_measure && item.unit_of_measure.trim()) {
                        // Create a new unit from the legacy unit string
                        const newUnit = await createUnitOfMeasure(item.unit_of_measure);
                        if (newUnit) {
                            processedItem.unit_of_measure_id = newUnit.id;
                            processedItem.unit_of_measure = newUnit.abbreviation;
                            console.log(`Created unit "${newUnit.name}" for line item: ${item.description}`);
                        } else {
                            // If unit creation fails, remove the field
                            delete processedItem.unit_of_measure_id;
                            console.warn(`Failed to create unit for "${item.unit_of_measure}", proceeding without unit ID`);
                        }
                    } else {
                        // No legacy unit string to keep - remove the field (send null)
                        delete processedItem.unit_of_measure_id;
                        console.log(`No unit of measure for line item: ${item.description}, sending null`);
                    }
                } else if (item.unit_of_measure_id === 'none' || !item.unit_of_measure_id) {
                    // Remove the field entirely instead of sending empty string
                    delete processedItem.unit_of_measure_id;
                }

                // Note: Draft products have been eliminated - all products are now created as active

                processedLineItems.push(processedItem);
            }

            // Prepare the update data including processed line items
            const updateData = {
                ...formData,
                line_items: processedLineItems
            };

            // First, activate any draft products that have categories selected
            for (const draftProduct of draftProductsToActivate) {
                try {
                    await apiFetch(`/api/products/${draftProduct.productId}`, {
                        method: 'PATCH',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            cost_category_id: draftProduct.categoryId,
                            status: 'active'
                        }),
                    });
                } catch (error) {
                    console.error('Error activating draft product:', error);
                    // Continue with invoice save even if product activation fails
                }
            }

            const navDir = isOutbound ? 'OUTBOUND' : 'INBOUND';
            let response: Response;
            if (isNew) {
                response = await apiFetch(`/api/invoices/?nav_direction=${navDir}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(updateData),
                });
            } else {
                response = await apiFetch(`/api/invoices/${id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(updateData),
                });
            }

            if (response.ok) {
                const updatedInvoice = await response.json();
                setInvoice(updatedInvoice);
                setLineItems(updatedInvoice.line_items || []);
                setEditMode(false);

                let successMessage = isNew ? 'Invoice created successfully!' : 'Invoice updated successfully!';
                if (draftProductsToActivate.length > 0) {
                    successMessage += ` ${draftProductsToActivate.length} draft product(s) activated.`;
                }

                if (isNew) {
                    // Navigate to the created invoice detail page
                    navigate({ to: '/invoices/$id', params: { id: updatedInvoice.id }, search: { nav_direction: navDir } as any });
                    setMessage({ type: 'success', text: successMessage });
                    setSaving(false);
                    return;
                }

                // Check if invoice can be automatically verified
                // This happens when all line items have active products and no draft products remain
                try {
                    const checkResponse = await apiFetch(`/api/invoices/${id}/check-verification-status`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    });

                    if (checkResponse.ok) {
                        const verificationResult = await checkResponse.json();
                        if (verificationResult.status_updated) {
                            successMessage += ' Invoice automatically verified!';
                            // Refresh the invoice to get the updated status
                            await fetchInvoice();
                        }
                    }
                } catch (error) {
                    console.error('Error checking invoice verification status:', error);
                    // Don't fail the save operation if verification check fails
                }

                setMessage({ type: 'success', text: successMessage });
            } else {
                const errorData = await response.json();
                let errorMessage = 'Failed to update invoice';

                // Handle different error response formats
                if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.detail) {
                    errorMessage = errorData.detail;
                } else if (errorData.errors && Array.isArray(errorData.errors)) {
                    errorMessage = `Validation errors: ${errorData.errors.join('; ')}`;
                }

                setMessage({ type: 'error', text: errorMessage });
            }
        } catch (error: any) {
            console.error('Save error:', error);
            setMessage({
                type: 'error',
                text: `Network error: ${error.message || 'Please check your connection and try again'}`
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setEditMode(false);
        // Reset form data to original values
        if (invoice) {
            setFormData({
                invoice_number: invoice.invoice_number || '',
                vendor_id: invoice.vendor_id || '',
                cost_center_id: invoice.cost_center_id || '',
                total_amount: invoice.total_amount?.toString() || '',
                net_amount: invoice.net_amount?.toString() || '',
                tax_amount: invoice.tax_amount?.toString() || '',
                currency: invoice.currency || 'USD',
                invoice_date: toDateInputValue(invoice.invoice_date),
                service_date: toDateInputValue(invoice.service_date),
                due_date: toDateInputValue(invoice.due_date),
                payment_method: invoice.payment_method || '',
            });
            setLineItems(invoice.line_items || []);
        }
    };

    const handleReprocess = async () => {
        setReprocessing(true);
        setMessage(null);

        try {
            const response = await apiFetch(`/api/invoices/${id}/reprocess`, {
                method: 'POST',
            });

            if (response.ok) {
                const result = await response.json();

                // Show success message with details
                if (result.message) {
                    setMessage({
                        type: 'success',
                        text: result.message + ' Refreshing invoice data...'
                    });
                } else {
                    setMessage({
                        type: 'success',
                        text: 'Invoice reprocessed successfully! Refreshing data...'
                    });
                }

                // Refresh the invoice data immediately
                setTimeout(async () => {
                    await fetchInvoice();
                    setMessage({
                        type: 'success',
                        text: 'Invoice data refreshed successfully!'
                    });

                    // Clear success message after a few seconds
                    setTimeout(() => {
                        setMessage(null);
                    }, 3000);
                }, 1000);

            } else {
                const errorData = await response.json();
                setMessage({
                    type: 'error',
                    text: errorData.detail || 'Failed to reprocess invoice'
                });
            }
        } catch (error) {
            setMessage({
                type: 'error',
                text: 'Network error occurred while reprocessing invoice'
            });
        } finally {
            setReprocessing(false);
        }
    };

    const handleRerunCategorization = async () => {
        setRerunningCategorization(true);
        setMessage(null);

        try {
            const response = await apiFetch(`/api/invoices/${id}/rerun-categorization`, {
                method: 'POST',
            });

            if (response.ok) {
                const result = await response.json();
                setMessage({
                    type: 'success',
                    text: `${result.message || 'Invoice categorization re-run successfully.'} Refreshing invoice data...`
                });

                await fetchInvoice();
                setMessage({
                    type: 'success',
                    text: 'Invoice categorization refreshed successfully.'
                });

                setTimeout(() => {
                    setMessage(null);
                }, 3000);
            } else {
                const errorData = await response.json();
                setMessage({
                    type: 'error',
                    text: errorData.detail || 'Failed to re-run categorization'
                });
            }
        } catch (error) {
            setMessage({
                type: 'error',
                text: 'Network error occurred while re-running categorization'
            });
        } finally {
            setRerunningCategorization(false);
        }
    };

    const handleUnmatchPhoto = async () => {
        if (!confirm(t('invoices.confirmUnmatchPhoto', 'finance'))) {
            return;
        }

        setUnmatchingPhoto(true);
        setMessage(null);

        try {
            const response = await apiFetch(`/api/gdrive-invoices/unmatch-invoice/${id}`, {
                method: 'POST',
            });

            if (response.ok) {
                const result = await response.json();
                setMessage({
                    type: 'success',
                    text: result.message || 'Photo unmatched successfully!'
                });

                // Refresh the invoice data to update the photo status
                await fetchInvoice();
            } else {
                const errorData = await response.json();
                setMessage({
                    type: 'error',
                    text: errorData.detail || 'Failed to unmatch photo'
                });
            }
        } catch (error) {
            setMessage({
                type: 'error',
                text: 'Network error occurred while unmatching photo'
            });
        } finally {
            setUnmatchingPhoto(false);
        }
    };

    const handleDeleteClick = () => {
        setShowDeleteDialog(true);
    };

    const isValidInvoiceDocument = (file: File): boolean => {
        const allowedMimeTypes = new Set([
            'application/pdf',
            'image/jpeg',
            'image/png',
            'image/gif',
            'image/webp',
            'image/heic',
            'image/heif',
            'image/bmp',
            'image/x-ms-bmp',
            'image/x-windows-bmp',
        ]);
        const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif', '.bmp', '.dib'];
        const lowerName = file.name.toLowerCase();

        return (
            allowedMimeTypes.has((file.type || '').toLowerCase()) ||
            allowedExtensions.some((ext) => lowerName.endsWith(ext))
        );
    };

    const handleInvoiceDocumentUpload = async (file: File) => {
        if (!invoice?.id || isNew) return;
        if (!isValidInvoiceDocument(file)) {
            setMessage({
                type: 'error',
                text: 'Invalid file type. Please upload a PDF or image file (including BMP).',
            });
            return;
        }

        setUploadingDocument(true);
        setMessage(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await apiFetch(`/api/invoices/${invoice.id}/document`, {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                setMessage({ type: 'success', text: 'Document uploaded successfully.' });
                await fetchInvoice();
            } else {
                let errorMessage = 'Failed to upload document';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.detail || errorData.message || errorMessage;
                } catch {
                    // Keep default message
                }
                setMessage({ type: 'error', text: errorMessage });
            }
        } catch (error) {
            setMessage({
                type: 'error',
                text: 'Network error occurred while uploading document',
            });
        } finally {
            setUploadingDocument(false);
            if (documentFileInputRef.current) {
                documentFileInputRef.current.value = '';
            }
        }
    };

    const handleDocumentDrop = async (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDocumentDragActive(false);
        const droppedFile = event.dataTransfer.files?.[0];
        if (droppedFile) {
            await handleInvoiceDocumentUpload(droppedFile);
        }
    };

    const handleDeleteConfirm = async () => {
        setDeleting(true);
        setMessage(null);
        setShowDeleteDialog(false);

        try {
            const response = await apiFetch(`/api/invoices/${id}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                // Navigate back to invoices list after successful deletion
                navigate({ to: '/invoices' });
            } else {
                const errorData = await response.json();
                setMessage({
                    type: 'error',
                    text: errorData.detail || 'Failed to delete invoice'
                });
            }
        } catch (error) {
            setMessage({
                type: 'error',
                text: 'Network error occurred while deleting invoice'
            });
        } finally {
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-96">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <p className="ml-2 text-muted-foreground">{t('invoices.loading', 'finance')}</p>
                </div>
            </Layout>
        );
    }

    if (!invoice && !isNew) {
        return (
            <Layout>
                <div className="text-center py-8">
                    <p className="text-muted-foreground">{t('invoices.notFound', 'finance')}</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <Button variant="ghost" onClick={handleGoBack}>
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            {t('actions.back', 'common')}
                        </Button>
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight">
                                {t('invoices.invoice', 'finance')} {invoice ? (invoice.invoice_number || `#${invoice.id.slice(0, 8)}`) : 'New'}
                            </h1>
                            <div className="mt-2 flex items-center gap-2">
                                <span className="text-muted-foreground">{t('invoices.status', 'finance')}:</span>
                                <Badge variant={
                                    invoice?.status === 'processed' || invoice?.status === 'verified' ? 'default' :
                                        invoice?.status === 'processing' ? 'secondary' :
                                            invoice?.status === 'failed' ? 'destructive' : 'outline'
                                }
                                    className={
                                        invoice?.status === 'verified' ? 'bg-blue-500 text-white dark:bg-blue-600' : ''}
                                >
                                    {invoice?.status === 'verified' ? <BadgeCheckIcon /> : ''}
                                    {invoice?.status || t('messages.na', 'common')}
                                </Badge>
                            </div>
                        </div>
                    </div>
                    <div className="flex space-x-2">
                        {!editMode ? (
                            <>
                                <Button
                                    variant="outline"
                                    onClick={() => setRecognitionScheduleModalOpen(true)}
                                    disabled={reprocessing}
                                    className="relative"
                                >
                                    <Calendar className="h-4 w-4 mr-2" />
                                    {t('invoices.recognitionSchedule', 'finance')}
                                    {hasRecognitionSchedule && (
                                        <span className="absolute -top-1 -right-1 h-3 w-3 bg-blue-500 rounded-full border-2 border-white" />
                                    )}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={handleReprocess}
                                    disabled={reprocessing}
                                    hidden={true}
                                >
                                    {reprocessing ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            {t('invoices.reprocessing', 'finance')}
                                        </>
                                    ) : (
                                        t('invoices.reprocess', 'finance')
                                    )}
                                </Button>
                                {!isNew && invoice?.nav_direction && (
                                    <Button
                                        variant="outline"
                                        onClick={handleRerunCategorization}
                                        disabled={reprocessing || rerunningCategorization}
                                    >
                                        {rerunningCategorization ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                {t('invoices.rerun-cat-progress', 'finance')}
                                            </>
                                        ) : (
                                            <>
                                                <RefreshCw className="h-4 w-4 mr-2" />
                                                {t('invoices.rerun-cat', 'finance')}
                                            </>
                                        )}
                                    </Button>
                                )}
                                {invoice?.has_invoice_photo && (
                                    <Button
                                        variant="destructive_outline"
                                        onClick={handleUnmatchPhoto}
                                        disabled={reprocessing || unmatchingPhoto}
                                    >
                                        {unmatchingPhoto ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                {t('invoices.unmatching', 'finance')}
                                            </>
                                        ) : (
                                            t('invoices.unmatchPhoto', 'finance')
                                        )}
                                    </Button>
                                )}
                                <Button onClick={() => setEditMode(true)} disabled={reprocessing}>
                                    {t('invoices.editInvoice', 'finance')}
                                </Button>
                                <Button
                                    variant="destructive"
                                    onClick={handleDeleteClick}
                                    disabled={reprocessing || deleting}
                                >
                                    {deleting ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            {t('actions.deleting', 'common')}
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 className="h-4 w-4 mr-2" />
                                            {t('actions.delete', 'common')}
                                        </>
                                    )}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button variant="outline" onClick={handleCancel}>
                                    {t('actions.cancel', 'common')}
                                </Button>
                                <div className="flex flex-col items-end gap-2">

                                    <Button
                                        onClick={handleSave}
                                        disabled={saving || validateForm().length > 0}
                                    >
                                        {saving ? t('actions.saving', 'common') : t('actions.saveChanges', 'common')}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Message */}
                {message && (
                    <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
                        {message.type === 'success' ? (
                            <CheckCircle className="h-4 w-4" />
                        ) : (
                            <AlertCircle className="h-4 w-4" />
                        )}
                        <AlertDescription>{message.text}</AlertDescription>
                    </Alert>
                )}

                {/* validation errors */}
                {(invoice?.validation_errors?.length ?? 0) > 0 && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                            <div className="space-y-2">
                                <h4 className="font-semibold">{t('messages.validationErrors', 'common')}</h4>
                                <ul className="list-disc list-inside space-y-1">
                                    {invoice?.validation_errors?.map((error, index) => {
                                        // Handle different error formats
                                        let errorText = '';
                                        if (typeof error === 'string') {
                                            errorText = error;
                                        } else if (typeof error === 'object' && error !== null) {
                                            // Handle Pydantic validation error objects
                                            if (error.msg) {
                                                const location = error.loc ? error.loc.join(' -> ') : '';
                                                errorText = location ? `${location}: ${error.msg}` : error.msg;
                                            } else {
                                                errorText = JSON.stringify(error);
                                            }
                                        } else {
                                            errorText = String(error);
                                        }

                                        return (
                                            <li key={index} className="text-sm">
                                                {errorText}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </AlertDescription>
                    </Alert>
                )}

                {/* Invoice Details - Compact Horizontal Layout */}
                <div className="bg-muted/50 rounded-lg border p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                        {/* Invoice Information Block */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold">{t('invoices.invoiceInformation', 'finance')}</h3>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        {t('invoices.invoiceNumber', 'finance')}
                                    </label>
                                    {editMode ? (
                                        <Input
                                            type="text"
                                            value={formData.invoice_number}
                                            onChange={(e) => handleInputChange('invoice_number', e.target.value)}
                                            className={`text-sm ${fieldErrors['invoice_number'] ? 'border-red-500 bg-red-50' : ''}`}
                                        />
                                    ) : (
                                        <p className="text-sm">{invoice?.invoice_number || t('messages.na', 'common')}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        {isOutbound ? t('invoices.customer', 'finance') : t('invoices.vendor', 'finance')}
                                    </label>
                                    {isOutbound ? (
                                        <p className="text-sm">{invoice?.customer?.name || t('invoices.unknownCustomer', 'finance')}</p>
                                    ) : editMode ? (
                                        <VendorCombobox
                                            vendors={vendors}
                                            value={formData.vendor_id}
                                            onValueChange={(value) => handleInputChange('vendor_id', value)}
                                            onVendorCreated={(newVendor) => {
                                                setVendors(prev => [...prev, newVendor]);
                                                handleInputChange('vendor_id', newVendor.id);
                                            }}
                                            placeholder={t('invoices.selectVendor', 'finance')}
                                            hasError={!!fieldErrors['vendor_id']}
                                        />
                                    ) : (
                                        <p className="text-sm">{invoice?.vendor?.name || t('invoices.unknownVendor', 'finance')}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        {isOutbound ? t('invoices.vendor', 'finance') : t('invoices.costCenter', 'finance')}
                                    </label>
                                    {isOutbound ? (
                                        editMode ? (
                                            <VendorCombobox
                                                vendors={vendors}
                                                value={formData.vendor_id}
                                                onValueChange={(value) => handleInputChange('vendor_id', value)}
                                                onVendorCreated={(newVendor) => {
                                                    setVendors(prev => [...prev, newVendor]);
                                                    handleInputChange('vendor_id', newVendor.id);
                                                }}
                                                placeholder={t('invoices.selectVendor', 'finance')}
                                                hasError={!!fieldErrors['vendor_id']}
                                            />
                                        ) : (
                                            <p className="text-sm">{invoice?.vendor?.name || t('invoices.unknownVendor', 'finance')}</p>
                                        )
                                    ) : (
                                        editMode ? (
                                            <Select value={formData.cost_center_id || "none"} onValueChange={(value) => handleInputChange('cost_center_id', value === "none" ? "" : value)}>
                                                <SelectTrigger className={`text-sm w-52 max-w-52 ${fieldErrors['cost_center_id'] ? 'border-red-500 bg-red-50' : ''}`}>
                                                    <SelectValue placeholder={t('invoices.selectCostCenter', 'finance')} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">{t('invoices.noCostCenter', 'finance')}</SelectItem>
                                                    {costCenters.map((costCenter) => (
                                                        <SelectItem key={costCenter.id} value={costCenter.id}>
                                                            {costCenter.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <p className="text-sm">{invoice?.cost_center?.name || t('invoices.notAssigned', 'finance')}</p>
                                        )
                                    )}
                                </div>

                                {/* Receiving Company (Customer) - Only for inbound invoices without nav_credentials_id */}
                                {!isOutbound && !invoice?.nav_credentials_id && (
                                    <div>
                                        <label className="block text-xs font-medium mb-1">
                                            Receiving Company
                                        </label>
                                        {editMode ? (
                                            <Select
                                                value={formData.customer_id || "none"}
                                                onValueChange={(value) => handleInputChange('customer_id', value === "none" ? "" : value)}
                                            >
                                                <SelectTrigger className="text-sm">
                                                    <SelectValue placeholder="Select a company" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">Not Assigned</SelectItem>
                                                    {companies.map((company) => (
                                                        <SelectItem key={company.id} value={company.id}>
                                                            {company.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <p className="text-sm">{invoice?.customer?.name || 'Not Assigned'}</p>
                                        )}
                                    </div>
                                )}

                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        {t('units.currency', 'common')}
                                    </label>
                                    {editMode ? (
                                        <Select value={formData.currency} onValueChange={(value) => handleCurrencyChange(value)}>
                                            <SelectTrigger className={`text-sm ${fieldErrors['currency'] ? 'border-red-500 bg-red-50' : ''}`}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {availableCurrencies.map((code) => (
                                                    <SelectItem key={code} value={code}>{code}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <p className="text-sm">{invoice?.currency}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        {t('invoices.paymentMethod', 'finance')}
                                    </label>
                                    {editMode ? (
                                        <Input
                                            type="text"
                                            value={formData.payment_method}
                                            onChange={(e) => handleInputChange('payment_method', e.target.value)}
                                            className="text-sm"
                                        />
                                    ) : (
                                        <p className="text-sm">{invoice?.payment_method || t('messages.na', 'common')}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Financial Details Block */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold">{t('invoices.financialDetails', 'finance')}</h3>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        {t('invoices.netAmount', 'finance')}
                                    </label>
                                    {editMode ? (
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={formData.net_amount}
                                            onChange={(e) => handleInputChange('net_amount', e.target.value)}
                                            className="text-sm"
                                        />
                                    ) : (
                                        <p className="text-sm">{formatAmountDetailed(invoice?.net_amount ?? 0)}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        {t('invoices.vatAmount', 'finance')}
                                    </label>
                                    {editMode ? (
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={formData.tax_amount}
                                            onChange={(e) => handleInputChange('tax_amount', e.target.value)}
                                            className="text-sm"
                                        />
                                    ) : (
                                        <p className="text-sm">{formatAmountDetailed(invoice?.tax_amount ?? 0)}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        {t('invoices.totalAmount', 'finance')} ({t('invoices.gross', 'finance')})
                                    </label>
                                    {editMode ? (
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={formData.total_amount}
                                            onChange={(e) => handleInputChange('total_amount', e.target.value)}
                                            className="text-sm"
                                        />
                                    ) : (
                                        <p className="text-sm font-semibold">{formatAmountDetailed(invoice?.total_amount ?? 0)}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Dates Block */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold">{t('invoices.dates', 'finance')}</h3>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        {t('invoices.invoiceDate', 'finance')}
                                    </label>
                                    {editMode ? (
                                        <Input
                                            type="date"
                                            value={formData.invoice_date}
                                            onChange={(e) => handleInputChange('invoice_date', e.target.value)}
                                            className={`text-sm ${fieldErrors['invoice_date'] ? 'border-red-500 bg-red-50' : ''}`}
                                        />
                                    ) : (
                                        <p className="text-sm">{formatDate(invoice?.invoice_date ?? '')}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        {t('invoices.serviceDate', 'finance')}
                                    </label>
                                    {editMode ? (
                                        <Input
                                            type="date"
                                            value={formData.service_date}
                                            onChange={(e) => handleInputChange('service_date', e.target.value)}
                                            className="text-sm"
                                        />
                                    ) : (
                                        <p className="text-sm">{formatDate(invoice?.service_date ?? '')}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-medium mb-1">
                                        {t('invoices.dueDate', 'finance')}
                                    </label>
                                    {editMode ? (
                                        <Input
                                            type="date"
                                            value={formData.due_date}
                                            onChange={(e) => handleInputChange('due_date', e.target.value)}
                                            className="text-sm"
                                        />
                                    ) : (
                                        <p className="text-sm">{formatDate(invoice?.due_date ?? '')}</p>
                                    )}
                                </div>
                                {/* Empty space for 2x2 grid balance */}
                                <div></div>
                            </div>
                        </div>

                        {/* Tags Section */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold">{t('tags.tags', 'common')}</h3>
                            {invoice?.id && (
                                <TagSelector
                                    entityType="invoices"
                                    entityId={invoice.id}
                                    selectedTags={invoiceTags}
                                    onChange={setInvoiceTags}
                                />
                            )}
                        </div>

                    </div>
                </div>

                {/* Main Content - Two Column Layout: Line Items + Invoice Viewer */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column - Line Items */}
                    <div className="space-y-4">
                        {/* Line Items */}
                        {(lineItems.length > 0 || editMode) && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-semibold">Line Items</h3>
                                    {editMode && (
                                        <Button onClick={addLineItem}>
                                            Add Row
                                        </Button>
                                    )}
                                </div>

                                {editMode ? (
                                    <div className="">
                                        <table className="w-full text-sm">
                                            <thead className="bg-muted/50">
                                                <tr>
                                                    <th className="text-left p-2 font-medium text-xs ">Description</th>
                                                    <th className="text-left p-2 font-medium text-xs w-24">Category</th>
                                                    <th className="text-right p-2 font-medium text-xs w-16">Qty</th>
                                                    <th className="text-left p-2 font-medium text-xs w-20">Unit</th>
                                                    <th className="text-right p-2 font-medium text-xs w-20">Price</th>
                                                    <th className="text-right p-2 font-medium text-xs w-16">VAT%</th>
                                                    <th className="text-right p-2 font-medium text-xs w-20">Total</th>
                                                    <th className="w-8"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {lineItems.map((item, index) => (
                                                    <tr key={item.id || index} className="border-b hover:bg-muted/25">
                                                        <td className="p-1">
                                                            <input
                                                                type="text"
                                                                value={item.description || ''}
                                                                onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                                                                className={`w-full p-1 text-xs border-0 bg-transparent focus:bg-background focus:ring-1 focus:ring-ring rounded ${fieldErrors[`line_item_${index}_description`] ? 'bg-red-50 ring-1 ring-red-500' : ''}`}
                                                                placeholder={t('invoices.description', 'finance')}
                                                            />
                                                        </td>
                                                        <td className="p-1">
                                                            <CategoryDropdown
                                                                selectedCategoryId={item.category_id || null}
                                                                onCategoryChange={(categoryId) => handleLineItemChange(index, 'category_id', categoryId || '')}
                                                                placeholder={t('forms.category', 'common')}
                                                                includeAllOption={false}
                                                                className={fieldErrors[`line_item_${index}_category_id`] ? 'border-red-500 bg-red-50' : ''}
                                                            />
                                                        </td>
                                                        <td className="p-1">
                                                            <input
                                                                type="number"
                                                                value={item.quantity || ''}
                                                                onChange={(e) => handleLineItemCalculation(index, 'quantity', e.target.value)}
                                                                className={`w-full p-1 text-xs text-right border-0 bg-transparent focus:bg-background focus:ring-1 focus:ring-ring rounded ${fieldErrors[`line_item_${index}_quantity`] ? 'bg-red-50 ring-1 ring-red-500' : ''}`}
                                                                placeholder="0"
                                                            />
                                                        </td>
                                                        <td className="p-1">
                                                            <Select
                                                                value={item.unit_of_measure_id || "keep_legacy"}
                                                                onValueChange={(value) => handleLineItemChange(index, 'unit_of_measure_id', value || '')}
                                                            >
                                                                <SelectTrigger className={`h-6 text-xs border-0 bg-transparent focus:bg-background focus:ring-1 focus:ring-ring ${fieldErrors[`line_item_${index}_unit_of_measure_id`] ? 'bg-red-50 ring-1 ring-red-500' : ''}`}>
                                                                    <SelectValue placeholder={t('invoices.unitOfMeasure', 'finance')} />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="keep_legacy">
                                                                        {t('invoices.keepAs', 'finance')} "{item.unit_of_measure || t('messages.na', 'common')}"
                                                                    </SelectItem>
                                                                    <SelectItem value="none">{t('messages.none', 'common')}</SelectItem>
                                                                    {['volume', 'weight', 'count', 'length'].map(unitType => {
                                                                        const unitsOfType = units.filter((unit: any) => unit.unit_type === unitType);
                                                                        if (unitsOfType.length === 0) return null;

                                                                        return (
                                                                            <div key={unitType}>
                                                                                <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase">
                                                                                    {unitType}
                                                                                </div>
                                                                                {unitsOfType.map((unit: any) => (
                                                                                    <SelectItem key={unit.id} value={unit.id}>
                                                                                        {unit.abbreviation} - {unit.name}
                                                                                    </SelectItem>
                                                                                ))}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </SelectContent>
                                                            </Select>
                                                        </td>
                                                        <td className="p-1">
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={item.unit_price || ''}
                                                                onChange={(e) => handleLineItemCalculation(index, 'unit_price', e.target.value)}
                                                                className={`w-full p-1 text-xs text-right border-0 bg-transparent focus:bg-background focus:ring-1 focus:ring-ring rounded ${fieldErrors[`line_item_${index}_unit_price`] ? 'bg-red-50 ring-1 ring-red-500' : ''}`}
                                                                placeholder="0.00"
                                                            />
                                                        </td>
                                                        <td className="p-1">
                                                            <Select
                                                                value={item.vat_rate ? Number(item.vat_rate).toString() : ''}
                                                                onValueChange={(value) => handleLineItemCalculation(index, 'vat_rate', value)}
                                                            >
                                                                <SelectTrigger className={`w-full h-7 text-xs border-0 bg-transparent focus:bg-background focus:ring-1 focus:ring-ring ${fieldErrors[`line_item_${index}_vat_rate`] ? 'bg-red-50 ring-1 ring-red-500' : ''}`}>
                                                                    <SelectValue placeholder="VAT %" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {vatRates.map((rate) => (
                                                                        <SelectItem key={rate} value={rate.toString()}>
                                                                            {rate}%
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </td>
                                                        <td className="p-1">
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={item.line_total || ''}
                                                                onChange={(e) => handleLineItemChange(index, 'line_total', e.target.value)}
                                                                className="w-full p-1 text-xs text-right border-0 bg-transparent focus:bg-background focus:ring-1 focus:ring-ring rounded font-medium"
                                                                placeholder="0.00"
                                                            />
                                                        </td>
                                                        {/*
                                                        <td className="p-1">
                                                            <input
                                                                type="text"
                                                                value={item.product_code || ''}
                                                                onChange={(e) => handleLineItemChange(index, 'product_code', e.target.value)}
                                                                className="w-full p-1 text-xs border-0 bg-transparent focus:bg-background focus:ring-1 focus:ring-ring rounded"
                                                                placeholder="Code"
                                                            />
                                                        </td>
                                                        */}
                                                        <td className="p-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => removeLineItem(index)}
                                                                className="h-6 w-6 p-0 text-destructive hover:text-destructive/80"
                                                            >
                                                                <Trash2 className="w-3 h-3" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Description</TableHead>
                                                <TableHead>Category</TableHead>
                                                <TableHead>Qty</TableHead>
                                                <TableHead>Unit Price</TableHead>
                                                <TableHead>VAT %</TableHead>
                                                <TableHead>Total</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {lineItems.map((item, index) => (
                                                <TableRow key={item.id || index}>
                                                    <TableCell className="w-sm text-wrap">
                                                        <div>
                                                            <div className="text-sm text-wrap">{item.description}</div>
                                                            {item.product_code && (
                                                                <div className="text-xs text-muted-foreground">Code: {item.product_code}</div>
                                                            )}

                                                            {item.product_match_status && item.product_match_status !== 'matched' && !item.product && (
                                                                <div className="text-xs">
                                                                    <Badge variant={
                                                                        item.product_match_status === 'needs_review' ? 'secondary' :
                                                                            item.product_match_status === 'pending' ? 'outline' : 'default'
                                                                    }>
                                                                        {item.product_match_status === 'needs_review' ? 'Needs Review' :
                                                                            item.product_match_status === 'pending' ? 'No Product' :
                                                                                item.product_match_status}
                                                                    </Badge>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="text-sm">
                                                            {isOutbound
                                                                ? (item.product?.revenue_category?.name || t('costCategories.uncategorized', 'finance'))
                                                                : (item.product?.cost_category?.name || item.category?.name || t('costCategories.uncategorized', 'finance'))}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="text-sm">
                                                            {item.quantity ? `${formatQuantity(item.quantity)} ${item.product?.base_unit?.abbreviation || item.unit_of_measure || ''}`.trim() : t('messages.na', 'common')}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="text-sm text-right">{formatAmountDetailed(item.unit_price)}</div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="text-sm text-right">
                                                            {formatVatRate(item.vat_rate)}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="text-sm font-medium text-right">{formatAmountDetailed(item.line_total)}</div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}

                                {/* Line Items Summary */}
                                {lineItems.length > 0 && (
                                    <div className="mt-4 pt-4">
                                        <div className="flex justify-end">
                                            <div className="w-64 space-y-2">
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-muted-foreground">{t('invoices.subtotal', 'finance')} ({t('invoices.net', 'finance')}):</span>
                                                    <span>
                                                        {formatAmountDetailed(lineItems.reduce((sum, item) => sum + (parseFloat(item.net_amount?.toString() || '0') || 0), 0))}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-muted-foreground">{t('general.vat', 'finance')}:</span>
                                                    <span>
                                                        {formatAmountDetailed(lineItems.reduce((sum, item) => sum + (parseFloat(item.vat_amount?.toString() || '0') || 0), 0))}
                                                    </span>
                                                </div>
                                                <Separator />
                                                <div className="flex justify-between text-base font-semibold pt-2">
                                                    <span>
                                                        {t('general.total', 'finance')} ({t('invoices.gross', 'finance')}):</span>
                                                    <span>
                                                        {formatAmountDetailed(lineItems.reduce((sum, item) => sum + (parseFloat(item.line_total?.toString() || '0') || 0), 0))}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Right Column - Invoice Document & Events */}
                    <div className="space-y-2">
                        <Tabs defaultValue="document" className="w-full">
                            <TabsList className="w-full">
                                <TabsTrigger value="document" className="flex-1">
                                    {t('invoices.documentTab', 'finance')}
                                </TabsTrigger>
                                <TabsTrigger value="events" className="flex-1">
                                    {t('invoices.eventsTab', 'finance')}
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="document" className="space-y-2 mt-4">
                                <div
                                    className={`rounded-lg border p-0 transition-colors ${isDocumentDragActive ? 'border-blue-500 bg-blue-50/40' : ''}`}
                                    onDragOver={(event) => {
                                        event.preventDefault();
                                        if (!isDocumentDragActive) setIsDocumentDragActive(true);
                                    }}
                                    onDragLeave={(event) => {
                                        event.preventDefault();
                                        setIsDocumentDragActive(false);
                                    }}
                                    onDrop={handleDocumentDrop}
                                >
                                    <div className="p-0">
                                        <DocumentViewer
                                            src={invoice?.file_path ? getFileUrl(invoice.file_path) : null}
                                            mimeType={invoice?.mime_type}
                                            alt={`Invoice ${invoice ? (invoice.invoice_number || invoice.id) : ''}`}
                                        />
                                    </div>
                                    <div className="border-t p-3 flex items-center justify-between gap-3">
                                        <span className="text-xs text-muted-foreground">
                                            Drag and drop a PDF/image here to upload
                                        </span>
                                        <input
                                            ref={documentFileInputRef}
                                            type="file"
                                            className="hidden"
                                            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.bmp,.dib,image/*,application/pdf"
                                            onChange={(e) => {
                                                const selectedFile = e.target.files?.[0];
                                                if (selectedFile) {
                                                    handleInvoiceDocumentUpload(selectedFile);
                                                }
                                            }}
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => documentFileInputRef.current?.click()}
                                            disabled={uploadingDocument || isNew}
                                        >
                                            {uploadingDocument ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                    Uploading...
                                                </>
                                            ) : (
                                                <>
                                                    <Upload className="h-4 w-4 mr-2" />
                                                    Upload file
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>

                                {/* File Information */}
                                <div className="rounded-lg border p-6">
                                    <h3 className="text-lg font-semibold mb-4">{t('invoices.fileInformation', 'finance')}</h3>
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">{t('invoices.filename', 'finance')}:</span>
                                            <span>{invoice?.original_filename || t('messages.na', 'common')}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">{t('invoices.fileSize', 'finance')}:</span>
                                            <span>{invoice?.file_size ? `${Math.round(parseInt(String(invoice.file_size)) / 1024)} KB` : t('messages.na', 'common')}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">{t('invoices.uploaded', 'finance')}:</span>
                                            <span>{formatDate(invoice?.created_at ?? '')}</span>
                                        </div>
                                    </div>

                                    {/* Download Button */}
                                    {invoice?.file_path && (
                                        <div className="mt-4 pt-4 border-t">
                                            <Button variant="secondary" asChild>
                                                <a
                                                    href={invoice?.file_path ? (getFileUrl(invoice.file_path) || undefined) : undefined}
                                                    download={invoice?.original_filename}
                                                    className="inline-flex items-center"
                                                >
                                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    {t('actions.downloadFile', 'common')}
                                                </a>
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </TabsContent>

                            <TabsContent value="events" className="mt-4">
                                <div className="rounded-lg border">
                                    {invoice?.id && (
                                        <EventHistory
                                            objectType="invoices"
                                            objectId={invoice.id}
                                            autoLoad={false}
                                        />
                                    )}
                                </div>
                            </TabsContent>
                        </Tabs>
                    </div>
                </div>
            </div>

            {/* Recognition Schedule Modal */}
            {
                invoice && (
                    <RecognitionScheduleModal
                        open={recognitionScheduleModalOpen}
                        onClose={() => setRecognitionScheduleModalOpen(false)}
                        invoiceId={invoice.id}
                        invoiceAmount={invoice.net_amount || invoice.total_amount || 0}
                        invoiceCurrency={invoice.currency}
                        invoiceVendor={invoice.vendor?.name}
                        invoiceNumber={invoice.invoice_number}
                        onSaved={() => {
                            // Refresh recognition schedule status
                            checkRecognitionSchedule();
                        }}
                    />
                )
            }

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent className='w-max'>
                    <DialogHeader>
                        <DialogTitle>Delete Invoice</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this invoice? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>

                    {invoice && (
                        <div className="space-y-2 py-4">
                            <div className="text-sm">
                                <span className="font-medium">Invoice Number:</span> {invoice.invoice_number || 'N/A'}
                            </div>
                            <div className="text-sm">
                                <span className="font-medium">Vendor:</span> {invoice.vendor?.name || invoice.customer?.name || 'N/A'}
                            </div>
                            <div className="text-sm">
                                <span className="font-medium">Amount:</span> {invoice.total_amount !== undefined ? formatAmountDetailed(invoice.total_amount, invoice.currency) : 'N/A'}
                            </div>
                            <div className="text-sm">
                                <span className="font-medium">Date:</span> {invoice.invoice_date ? formatDate(invoice.invoice_date) : 'N/A'}
                            </div>
                            {lineItems && lineItems.length > 0 && (
                                <div className="text-sm">
                                    <span className="font-medium">Line Items:</span> {lineItems.length}
                                </div>
                            )}
                            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                <p className="text-sm text-yellow-800">
                                    <strong>Note:</strong> The invoice data will be archived for audit purposes before deletion.
                                </p>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowDeleteDialog(false)}
                            disabled={deleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteConfirm}
                            disabled={deleting}
                        >
                            {deleting ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete Invoice
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout >
    );
}


export const Route = createFileRoute('/invoices/$id')({
    validateSearch: invoiceDetailSearchSchema,
    component: InvoiceDetailPage,
})
